import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { serviceService } from '../../../services/serviceService';
import Button from '../../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const ServicesGrid = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadServices();
    const unsubscribe = serviceService?.subscribeToChanges((payload) => {
      if (['INSERT', 'DELETE'].includes(payload?.eventType)) {
        loadServices();
      } else if (payload?.eventType === 'UPDATE') {
        setServices(prev => prev?.map(svc =>
          svc?.id === payload?.new?.id ? convertService(payload?.new) : svc
        ));
      }
    });
    return () => unsubscribe();
  }, []);

  const loadServices = async () => {
    try {
      const data = await serviceService?.getAll();
      setServices(data?.slice(0, 6));
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertService = (svc) => ({
    id: svc?.id,
    title: svc?.title,
    category: svc?.category,
    icon: svc?.icon,
    image: svc?.image,
    imageAlt: svc?.image_alt || svc?.imageAlt,
    description: svc?.description,
    color: svc?.color,
  });

  if (loading) {
    return (
      <section className="py-24 bg-white dark:bg-slate-900 flex justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-white/5">
      {/* CONTAINER ÉLARGI : max-w-[1920px] */}
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16">

          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full mb-6">
            <Icon name="Briefcase" size={16} className="text-amber-400" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Notre Expertise</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Solutions Complètes pour <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Votre Réussite
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Découvrez notre gamme complète de services professionnels conçus pour répondre à tous vos besoins d'affaires en République Démocratique du Congo.
          </p>
        </motion.div>

        {/* Services Grid Wide */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {services?.map((service, index) => (
            <motion.div
              key={service?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-blue-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-900/20"
            >
              {/* Image Header */}
              <div className="relative h-64 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-t from-white dark:from-slate-900 via-white/10 dark:via-slate-900/10 to-transparent z-10`}></div>
                <div className={`absolute inset-0 bg-blue-900/30 mix-blend-overlay z-10 group-hover:opacity-0 transition-opacity duration-500`}></div>

                <Image
                  src={service?.image}
                  alt={service?.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />

                {/* Floating Icon */}
                <div className="absolute top-6 right-6 z-20 w-14 h-14 rounded-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex items-center justify-center border border-slate-200 dark:border-white/10 group-hover:bg-blue-600 group-hover:text-white text-blue-400 transition-colors duration-300 shadow-lg">
                  <Icon name={service?.icon} size={28} />
                </div>
              </div>

              {/* Content */}
              <div className="p-10 relative z-20 -mt-6">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 group-hover:text-blue-400 transition-colors">
                  {service?.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-base mb-8 line-clamp-2 leading-relaxed">
                  {service?.description}
                </p>

                <div className="flex items-center justify-between pt-6 border-t border-slate-200 dark:border-slate-700/50">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {service?.category || 'Service Pro'}
                  </span>
                  <button className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white group-hover:gap-3 transition-all duration-300 group-hover:text-amber-400">
                    En savoir plus
                    <Icon name="ArrowRight" size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-20">

          <Button
            variant="outline"
            size="lg"
            iconName="Grid"
            iconPosition="left"
            onClick={() => navigate('/services-overview')}
            className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-slate-400 dark:hover:border-white/30 px-10 py-5 rounded-full transition-all text-lg font-medium">
            Voir tous nos services
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesGrid;